// Direct request/response helpers for the Twilio Agent admin settings modal
// (super-user only; the goserver enforces the SUPER_USER check on every
// mutation). These hit the /v2/twilio_call/admin/* endpoints and unwrap the
// standard house envelope (r.data.data), mirroring how support_inbox's
// api_helpers.js handles its own admin calls. Every call rejects with the axios
// error so callers can surface the server's `reason` via apiErrorReason.

import {
  get_api as getApi,
  post_api as postApi,
  put_api as putApi,
  delete_api as deleteApi
} from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';

// Mirror the getUrl helper in index.jsx: prefer the dedicated twilio_call base
// URL when configured, else fall back to the environment-aware CMS API URL
// ('/v2/...' auto-routes to the Go server).
const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return getApiUrl(endpoint);
};

const BASE = '/v2/twilio_call/admin';

// Pull the human-readable failure reason out of an axios error (house envelope
// {"status":"failure","reason":...}), falling back to a caller-supplied default.
export const apiErrorReason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  return (data && (data.reason || data.message)) || fallback;
};

// GET /teams → [{ uuid, name, members: [{ cms_user_uuid, name }] }].
export const fetchTeams = () =>
  getApi(getUrl(`${BASE}/teams`), true).then(
    (r) => (r.data && r.data.data && r.data.data.teams) || []
  );

// POST /teams { name } → created team. SUPER_USER only.
export const createTeam = (name) =>
  postApi(getUrl(`${BASE}/teams`), { name }, true).then((r) => (r.data && r.data.data) || {});

// PUT /teams/:uuid { name } — rename.
export const renameTeam = (uuid, name) =>
  putApi(getUrl(`${BASE}/teams/${uuid}`), { name }, true).then(
    (r) => (r.data && r.data.data) || {}
  );

// DELETE /teams/:uuid.
export const deleteTeam = (uuid) => deleteApi(getUrl(`${BASE}/teams/${uuid}`), true);

// POST /teams/:uuid/members { cms_user_uuid } — add a member.
export const addTeamMember = (teamUuid, cmsUserUuid) =>
  postApi(getUrl(`${BASE}/teams/${teamUuid}/members`), { cms_user_uuid: cmsUserUuid }, true);

// DELETE /teams/:uuid/members/:cms_user_uuid — remove a member.
export const removeTeamMember = (teamUuid, cmsUserUuid) =>
  deleteApi(getUrl(`${BASE}/teams/${teamUuid}/members/${cmsUserUuid}`), true);

// GET /agents → [{ uuid, name, email }]. CMS users eligible for team membership.
export const fetchAgents = () =>
  getApi(getUrl(`${BASE}/agents`), true).then(
    (r) => (r.data && r.data.data && r.data.data.agents) || []
  );

// GET /phone_numbers → ["+13203029642", ...]. The env-appropriate Twilio caller
// numbers, flat and deduped, that a number can be routed from. SUPER_USER only.
export const fetchPhoneNumbers = () =>
  getApi(getUrl(`${BASE}/phone_numbers`), true).then(
    (r) => (r.data && r.data.data && r.data.data.phone_numbers) || []
  );

// GET /caller_types → [{ value, label }]. The caller categories a rule can match
// (e.g. ANY, TEACHER, TRIAL_STUDENT, MEMBER_STUDENT, NOMNOT_STUDENT). The label is
// server-supplied and shown verbatim in the UI.
export const fetchCallerTypes = () =>
  getApi(getUrl(`${BASE}/caller_types`), true).then(
    (r) => (r.data && r.data.data && r.data.data.caller_types) || []
  );

// GET /routing → [{ phone_number, rules: [{ caller_type, teams: [{ id, team_uuid,
// name, position }] }] }]. Rules are pre-ordered (ANY first) and each rule's teams
// are pre-ordered by position.
export const fetchRouting = () =>
  getApi(getUrl(`${BASE}/routing`), true).then(
    (r) => (r.data && r.data.data && r.data.data.routing) || []
  );

// POST /routing { phone_number, team_uuid, caller_type } — append a team to the ring
// order for a (number, caller_type) rule. caller_type is one of the enum values.
export const addRouting = (phoneNumber, teamUuid, callerType) =>
  postApi(
    getUrl(`${BASE}/routing`),
    { phone_number: phoneNumber, team_uuid: teamUuid, caller_type: callerType },
    true
  ).then((r) => (r.data && r.data.data) || {});

// DELETE /routing/:id — remove a single routing row.
export const deleteRouting = (id) => deleteApi(getUrl(`${BASE}/routing/${id}`), true);

// PUT /routing/reorder { phone_number, caller_type, ids:[...] } — full ordered id list
// for that (number, caller_type) rule.
export const reorderRouting = (phoneNumber, callerType, ids) =>
  putApi(
    getUrl(`${BASE}/routing/reorder`),
    { phone_number: phoneNumber, caller_type: callerType, ids },
    true
  );

// ---------------------------------------------------------------- blocked numbers

// These live here rather than in a blocked_numbers/api.js of their own (which
// the design sketched) because getUrl, BASE and apiErrorReason above already
// own how this page talks to /v2/twilio_call/admin. A second copy of the base
// URL is the kind of duplication that silently drifts the first time the
// dedicated Twilio base URL changes.

// GET /blocked_numbers?status=active|all → the blocklist. Open to any Twilio
// Agent user; only the mutations below are CARE/SUPER_USER gated server-side.
export const fetchBlockedNumbers = (status = 'active') =>
  getApi(getUrl(`${BASE}/blocked_numbers?status=${status}`), true).then(
    (r) => (r.data && r.data.data) || []
  );

// GET /blocked_numbers/history → newest-first audit feed. Keyset paginated:
// pass the last row's created_date and id to get the next page. Offset paging
// would get slower every month on an append-only table.
export const fetchBlockedNumberHistory = ({ numberUuid, cursorDate, cursorId, limit } = {}) => {
  const params = new URLSearchParams();
  if (numberUuid) params.set('number_uuid', numberUuid);
  if (cursorDate) {
    params.set('cursor_date', cursorDate);
    params.set('cursor_id', String(cursorId || 0));
  }
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();

  return getApi(getUrl(`${BASE}/blocked_numbers/history${qs ? `?${qs}` : ''}`), true).then(
    (r) => (r.data && r.data.data) || []
  );
};

// POST /blocked_numbers { phone_number, reason } → { uuid, phone_number, state }.
// `state` is added | reactivated | already_active: re-blocking an already
// blocked number is a success carrying a different message, not an error, so
// the second care agent to react to a caller is told rather than shown a failure.
export const addBlockedNumber = (phoneNumber, reason) =>
  postApi(getUrl(`${BASE}/blocked_numbers`), { phone_number: phoneNumber, reason }, true).then(
    (r) => (r.data && r.data.data) || {}
  );

// POST /blocked_numbers/:uuid/deactivate — lift a block, keeping the row and
// its history so "has this caller been blocked before" stays answerable.
export const deactivateBlockedNumber = (uuid, reason) =>
  postApi(getUrl(`${BASE}/blocked_numbers/${uuid}/deactivate`), { reason }, true).then(
    (r) => (r.data && r.data.data) || {}
  );

// POST /blocked_numbers/:uuid/activate — re-apply a lifted block.
export const activateBlockedNumber = (uuid, reason) =>
  postApi(getUrl(`${BASE}/blocked_numbers/${uuid}/activate`), { reason }, true).then(
    (r) => (r.data && r.data.data) || {}
  );
